export class VideoUtils {
    static init() {
        // Initialize all video blocks on the page
        document.addEventListener('DOMContentLoaded', () => {
            const videoBlocks = document.querySelectorAll('.full-width-video');
            videoBlocks.forEach(block => {
                const blockId = block.dataset.blockid;
                if (blockId) {
                    new VideoUtils(blockId);
                }
            });
        });
    }

    constructor(blockId, options = {}) {
        if (!blockId) {
            console.error('No block ID provided');
            return;
        }

        this.blockId = blockId;
        this.$block = $(`.block-${blockId}`);
        
        if (!this.$block.length) {
            console.error(`Block with ID ${blockId} not found`);
            return;
        }

        // Debug log the entire data attributes
        console.log('Block data attributes:', this.$block.data());

        this.videoUrl = this.$block.attr('data-videourl');
        this.videoType = this.$block.attr('data-videotype'); // Get explicit video type if set
        
        if (!this.videoUrl) {
            console.error('No video URL provided for block:', blockId);
            console.log('Block element:', this.$block[0]);
            return;
        }

        console.log('Initializing video with URL:', this.videoUrl);

        this.options = {
            autoplay: options.autoplay || true,
            controls: options.controls || true,
        };

        // If video type is already set (for Vimeo), skip parsing
        if (this.videoType === 'vimeo') {
            this.videoId = this.videoUrl.split('/').pop();
        } else {
            this.parseVideo();
        }
        
        this.init();
    }

    parseVideo() {
        // YouTube regex
        const youtubeRegex = /^.*(?:(?:youtu\.be\/|v\/|vi\/|u\/\w\/|embed\/|shorts\/)|(?:(?:watch)?\?v(?:i)?=|\&v(?:i)?=))([^#\&\?]*).*/;
        // Vimeo regex
        const vimeoRegex = /(?:www\.|player\.)?vimeo.com\/(?:channels\/(?:\w+\/)?|groups\/(?:[^\/]*)\/videos\/|album\/(?:\d+)\/video\/|video\/|)(\d+)(?:[a-zA-Z0-9_\-]+)?/;

        let match;
        if ((match = this.videoUrl.match(youtubeRegex))) {
            this.videoType = 'youtube';
            this.videoId = match[1];
            console.log('Detected YouTube video:', this.videoId);
        } else if ((match = this.videoUrl.match(vimeoRegex))) {
            this.videoType = 'vimeo';
            this.videoId = match[1];
            console.log('Detected Vimeo video:', this.videoId);
        } else {
            console.error('Invalid video URL:', this.videoUrl);
        }
    }

    init() {
        const $playButton = this.$block.find('.banner-play-icon');
        
        if (!$playButton.length) {
            console.error('Play button not found');
            return;
        }

        $playButton.on('click', (e) => {
            e.preventDefault();
            console.log('Play button clicked');
            this.playVideo();
        });
    }

    async playVideo() {
        if (!this.videoId || !this.videoType) {
            console.error('No valid video ID or type');
            return;
        }

        console.log('Playing video:', this.videoType, this.videoId);

        const $overlay = this.$block.find('.overlay');
        const $existingEmbed = this.$block.find('.embed-responsive');

        if ($existingEmbed.length) {
            $overlay.fadeOut(() => {
                $overlay.addClass('hidden');
            });
            if (this.player) {
                this.player.play();
            }
            return;
        }

        const embedHtml = this.createEmbed();
        $overlay.fadeOut(() => {
            $overlay.addClass('hidden');
            this.$block.append(embedHtml);
            this.initializePlayer();
        });
    }

    createEmbed() {
        const url = this.getVideoUrl();
        return `
            <div class="embed-responsive embed-responsive-16by9">
                <iframe 
                    class="embed-responsive-item" 
                    src="${url}"
                    allow="autoplay; fullscreen" 
                    allowfullscreen
                ></iframe>
            </div>
        `;
    }

    getVideoUrl() {
        const params = new URLSearchParams({
            autoplay: '1',
            controls: this.options.controls ? '1' : '0'
        });

        if (this.videoType === 'youtube') {
            params.append('rel', '0');
            return `https://www.youtube.com/embed/${this.videoId}?${params.toString()}`;
        } else {
            return `https://player.vimeo.com/video/${this.videoId}?${params.toString()}`;
        }
    }

    async initializePlayer() {
        const iframe = this.$block.find('iframe')[0];

        if (this.videoType === 'youtube') {
            await this.loadYouTubeAPI();
            this.player = new YT.Player(iframe, {
                events: {
                    onStateChange: (event) => this.handleYouTubeStateChange(event)
                }
            });
        } else {
            await this.loadVimeoAPI();
            this.player = new Vimeo.Player(iframe);
            this.addVimeoListeners();
        }
    }

    addVimeoListeners() {
        this.player.on('pause', () => this.showOverlay());
        this.player.on('ended', () => this.showOverlay());
    }

    handleYouTubeStateChange(event) {
        if (event.data === YT.PlayerState.PAUSED || event.data === YT.PlayerState.ENDED) {
            this.showOverlay();
        }
    }

    showOverlay() {
        const $overlay = this.$block.find('.overlay');
        $overlay.removeClass('hidden').fadeIn();
    }

    async loadYouTubeAPI() {
        if (window.YT) return;

        return new Promise((resolve) => {
            const tag = document.createElement('script');
            tag.src = 'https://www.youtube.com/iframe_api';
            const firstScriptTag = document.getElementsByTagName('script')[0];
            firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
            window.onYouTubeIframeAPIReady = resolve;
        });
    }

    async loadVimeoAPI() {
        if (window.Vimeo) return;

        return new Promise((resolve) => {
            const tag = document.createElement('script');
            tag.src = 'https://player.vimeo.com/api/player.js';
            tag.onload = resolve;
            document.body.appendChild(tag);
        });
    }
}

// Auto-initialize all video blocks
VideoUtils.init(); 